import React, { useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Eye, EyeOff, Lock, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { resetPassword } from '../services/api';

const LOGO_URL = "/assets/logo-le-bleu-des-champs.png";

export const ResetPasswordPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [passwords, setPasswords] = useState({
    password: '',
    confirm: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (passwords.password !== passwords.confirm) {
      toast.error('Les mots de passe ne correspondent pas');
      return;
    }
    
    if (passwords.password.length < 6) {
      toast.error('Le mot de passe doit contenir au moins 6 caractères');
      return;
    }
    
    setLoading(true);
    try {
      await resetPassword(token, passwords.password);
      setSuccess(true);
      toast.success('Mot de passe réinitialisé avec succès');
    } catch (error) {
      if (error.response?.data?.detail) {
        toast.error(error.response.data.detail);
      } else {
        toast.error('Erreur lors de la réinitialisation');
      }
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
        <div className="max-w-md w-full text-center">
          <img src={LOGO_URL} alt="Le Bleu des Champs" className="h-16 mx-auto mb-6" />
          <h1 className="text-2xl font-bold text-[#1a3a5c] mb-4">Lien invalide</h1>
          <p className="text-slate-500 mb-6">
            Ce lien de réinitialisation est invalide ou a expiré.
          </p>
          <Link to="/connexion">
            <Button className="bg-[#00b4d8] hover:bg-[#009bb8]">
              Retour à la connexion
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
        <div className="max-w-md w-full text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-8 h-8 text-green-500" />
          </div>
          <h1 className="text-2xl font-bold text-[#1a3a5c] mb-4">Mot de passe réinitialisé!</h1>
          <p className="text-slate-500 mb-6">
            Votre mot de passe a été modifié avec succès. Vous pouvez maintenant vous connecter.
          </p>
          <Link to="/connexion">
            <Button className="bg-[#00b4d8] hover:bg-[#009bb8]">
              Se connecter
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
      <div className="max-w-md w-full">
        <Link to="/" className="inline-block mb-8">
          <img src={LOGO_URL} alt="Le Bleu des Champs" className="h-16" />
        </Link>
        
        <h1 className="text-3xl font-bold text-[#1a3a5c] mb-2">
          Nouveau mot de passe
        </h1>
        <p className="text-slate-500 mb-8">
          Créez un nouveau mot de passe pour votre compte
        </p>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <Label htmlFor="password">Nouveau mot de passe</Label>
            <div className="relative mt-2">
              <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <Input
                id="password"
                type={showPassword ? 'text' : 'password'}
                value={passwords.password}
                onChange={(e) => setPasswords({ ...passwords, password: e.target.value })}
                placeholder="••••••••"
                className="pl-12 pr-12 input-styled"
                data-testid="new-password-input"
                required
                minLength={6}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
          </div>

          <div>
            <Label htmlFor="confirm">Confirmer le mot de passe</Label>
            <div className="relative mt-2">
              <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <Input
                id="confirm"
                type={showConfirm ? 'text' : 'password'}
                value={passwords.confirm}
                onChange={(e) => setPasswords({ ...passwords, confirm: e.target.value })}
                placeholder="••••••••"
                className="pl-12 pr-12 input-styled"
                data-testid="confirm-password-input"
                required
              />
              <button
                type="button"
                onClick={() => setShowConfirm(!showConfirm)}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
              >
                {showConfirm ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
          </div>

          <Button
            type="submit"
            disabled={loading}
            className="w-full rounded-full bg-[#00b4d8] hover:bg-[#009bb8] text-white py-6 text-lg"
            data-testid="reset-submit-btn"
          >
            {loading ? 'Réinitialisation...' : 'Réinitialiser le mot de passe'}
          </Button>
        </form>

        <div className="mt-8 text-center">
          <Link to="/connexion" className="text-[#00b4d8] hover:underline">
            Retour à la connexion
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
